import React, { useState, useEffect } from 'react';
import { Card, Container, Nav, Table, Button, Form, InputGroup, Badge, Alert, Modal, Row, Col, Pagination } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../firebase';
import { ref, onValue, remove } from 'firebase/database';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaCalendarAlt, FaListUl, FaDumbbell, FaSearch, FaFilter, FaEdit, FaTrash } from 'react-icons/fa';

interface Exercise {
  id: string;
  name: string;
  sets: number;
  reps: number;
  weight: number;
  notes?: string;
}

interface Workout {
  id: string;
  date: string;
  duration: number;
  notes?: string;
  exercises?: Exercise[];
}

const WorkoutList = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [filteredWorkouts, setFilteredWorkouts] = useState<Workout[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [workoutToDelete, setWorkoutToDelete] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<{ message: string } | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [paginatedWorkouts, setPaginatedWorkouts] = useState<Workout[]>([]);

  useEffect(() => {
    if (!currentUser) return;

    // Fetch workouts
    const workoutsRef = ref(database, `workouts/${currentUser.uid}/workouts`);
    const unsubscribeWorkouts = onValue(workoutsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const workoutsList = Object.entries(data).map(([id, workout]: [string, any]) => ({
        id,
        ...workout,
        date: workout.date || new Date().toISOString().split('T')[0]
      })).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setWorkouts(workoutsList);
      setFilteredWorkouts(workoutsList);
    });

    return () => {
      unsubscribeWorkouts();
    };
  }, [currentUser]);

  useEffect(() => {
    // Apply filters
    let result = [...workouts];

    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(workout =>
        workout.notes?.toLowerCase().includes(term) ||
        (workout.exercises && workout.exercises.some(ex =>
          ex.name.toLowerCase().includes(term) ||
          ex.notes?.toLowerCase().includes(term)
        ))
      );
    }

    // Apply date filter
    if (dateFilter !== 'all') {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];

      if (dateFilter === 'today') {
        result = result.filter(workout => workout.date === todayStr);
      } else if (dateFilter === 'week') {
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 7);
        result = result.filter(workout => new Date(workout.date) >= weekAgo);
      } else if (dateFilter === 'month') {
        const monthAgo = new Date(today);
        monthAgo.setMonth(today.getMonth() - 1);
        result = result.filter(workout => new Date(workout.date) >= monthAgo);
      }
    }

    setFilteredWorkouts(result);

    // Calculate total pages
    const calculatedTotalPages = Math.ceil(result.length / itemsPerPage);
    setTotalPages(calculatedTotalPages > 0 ? calculatedTotalPages : 1);

    // Reset to first page when filters change
    setCurrentPage(1);
  }, [workouts, searchTerm, dateFilter, itemsPerPage]);

  // Update paginated workouts whenever filtered workouts or pagination settings change
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedWorkouts(filteredWorkouts.slice(startIndex, endIndex));
  }, [filteredWorkouts, currentPage, itemsPerPage]);

  const handleEditWorkout = (id: string) => {
    // Navigate to workout dashboard with the workout ID to edit
    navigate(`/dashboard/workouts?edit=${id}`);
  };

  const confirmDeleteWorkout = (id: string) => {
    setWorkoutToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteWorkout = async () => {
    console.log('Attempting to delete workout with ID:', workoutToDelete);

    if (!currentUser || !workoutToDelete) {
      console.error('Cannot delete workout: currentUser or workoutToDelete is missing');
      return;
    }

    try {
      console.log('Deleting workout at path:', `workouts/${currentUser.uid}/workouts/${workoutToDelete}`);
      const workoutRef = ref(database, `workouts/${currentUser.uid}/workouts/${workoutToDelete}`);
      await remove(workoutRef);

      setShowDeleteModal(false);
      setWorkoutToDelete(null);

      setAlertMessage({
        message: 'Workout erfolgreich gelöscht.'
      });
      setTimeout(() => setAlertMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting workout:', error);
      setAlertMessage({
        message: 'Fehler beim Löschen des Workouts.'
      });
    }
  };

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('de-DE', options);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const renderPagination = () => {
    const items = [];

    // Previous button
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
      />
    );

    // First page
    items.push(
      <Pagination.Item
        key={1}
        active={currentPage === 1}
        onClick={() => handlePageChange(1)}
      >
        1
      </Pagination.Item>
    );

    // Ellipsis if needed
    if (currentPage > 3) {
      items.push(<Pagination.Ellipsis key="ellipsis-1" />);
    }

    // Pages around current page
    for (let number = Math.max(2, currentPage - 1); number <= Math.min(totalPages - 1, currentPage + 1); number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    // Ellipsis if needed
    if (currentPage < totalPages - 2 && totalPages > 4) {
      items.push(<Pagination.Ellipsis key="ellipsis-2" />);
    }

    // Last page if not the first page
    if (totalPages > 1) {
      items.push(
        <Pagination.Item
          key={totalPages}
          active={currentPage === totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    // Next button
    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
      />
    );

    return <Pagination>{items}</Pagination>;
  };

  if (!currentUser) {
    return (
      <Container>
        <h2 className="mb-4">Workout Liste</h2>
        <Card>
          <Card.Body>
            <Card.Title style={{ color: '#1780a6' }}>Workouts</Card.Title>
            <p>Bitte melden Sie sich an, um Ihre Workouts anzuzeigen.</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <h2 className="mb-4">Workout Liste</h2>

      {/* Navigation Tabs */}
      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link
            as={Link}
            to="/dashboard/workouts"
            active={location.pathname === '/dashboard/workouts'}
          >
            <FaCalendarAlt className="me-2" />
            Dashboard
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            as={Link}
            to="/dashboard/workouts-list"
            active={location.pathname === '/dashboard/workouts-list'}
          >
            <FaListUl className="me-2" />
            Workout Liste
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            as={Link}
            to="/dashboard/workout-exercises"
            active={location.pathname === '/dashboard/workout-exercises'}
          >
            <FaDumbbell className="me-2" />
            Übungen
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {alertMessage && (
        <Alert
          variant="success"
          onClose={() => setAlertMessage(null)}
          dismissible
          className="mb-4"
        >
          {alertMessage.message}
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Body>
          <Card.Title style={{ color: '#1780a6' }} className="d-flex justify-content-between align-items-center mb-4">
            <span>Workouts</span>
            <div>

              <Link to="/dashboard/workouts">
                <Button variant="primary">
                  Neues Workout
                </Button>
              </Link>
            </div>
          </Card.Title>

          <Row className="mb-4">
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>

            <Col md={4}>
              <Form.Select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <option value="all">Alle Zeiten</option>
                <option value="today">Heute</option>
                <option value="week">Letzte Woche</option>
                <option value="month">Letzter Monat</option>
              </Form.Select>
            </Col>
          </Row>

          {filteredWorkouts.length > 0 ? (
            <>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Datum</th>

                    <th>Dauer</th>
                    <th>Übungen</th>
                    <th>Notizen</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedWorkouts.map((workout, index) => (
                    <tr key={workout.id || `workout_row_${index}`}>
                      <td>{formatDate(workout.date)}</td>

                      <td>{workout.duration} min</td>
                      <td>
                        {workout.exercises ? (
                          <span>{workout.exercises.length} Übungen</span>
                        ) : (
                          <span className="text-muted">Keine Übungen</span>
                        )}
                      </td>
                      <td>
                        {workout.notes && (
                          <div className="small text-muted">
                            {workout.notes.length > 30
                              ? `${workout.notes.substring(0, 30)}...`
                              : workout.notes}
                          </div>
                        )}
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => handleEditWorkout(workout.id)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => confirmDeleteWorkout(workout.id)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <div className="d-flex justify-content-between align-items-center mt-4">
                <div>
                  <Form.Select
                    className="d-inline-block"
                    style={{ width: 'auto' }}
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  >
                    <option value="10">10 pro Seite</option>
                    <option value="20">20 pro Seite</option>
                    <option value="50">50 pro Seite</option>
                    <option value="100">100 pro Seite</option>
                  </Form.Select>
                  <span className="ms-3">
                    Zeige {(currentPage - 1) * itemsPerPage + 1} bis {Math.min(currentPage * itemsPerPage, filteredWorkouts.length)} von {filteredWorkouts.length} Einträgen
                  </span>
                </div>

                <div>
                  {renderPagination()}
                </div>
              </div>
            </>
          ) : (
            <Alert variant="info">
              Keine Workouts gefunden. {searchTerm || dateFilter !== 'all' ? (
                <Button
                  variant="link"
                  className="p-0"
                  onClick={() => {
                    setSearchTerm('');
                    setDateFilter('all');
                  }}
                >
                  Filter zurücksetzen
                </Button>
              ) : (
                <Link to="/dashboard/workouts">
                  <Button variant="link" className="p-0">
                    Neues Workout hinzufügen
                  </Button>
                </Link>
              )}
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Workout löschen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Sind Sie sicher, dass Sie dieses Workout löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Abbrechen
          </Button>
          <Button variant="danger" onClick={handleDeleteWorkout}>
            Löschen
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default WorkoutList;
